import { RequestFailure } from './httpClient';
import type { GoogleProfile } from '../auth/types';

interface GoogleUserInfoResponse {
  sub?: string;
  name?: string;
  email?: string;
  picture?: string;
}

export async function fetchGoogleProfile(
  accessToken: string,
  signal?: AbortSignal
): Promise<GoogleProfile> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new RequestFailure(
      'network',
      'No internet connection. Check your network and try again.'
    );
  }

  let response: Response;
  try {
    response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal,
    });
  } catch {
    throw new RequestFailure(
      'network',
      'Unable to reach Google. Check your connection and try again.'
    );
  }

  if (response.status >= 500) {
    throw new RequestFailure(
      'server',
      'Google sign-in is temporarily unavailable. Please try again.'
    );
  }

  if (!response.ok) {
    throw new RequestFailure(
      'unknown',
      'Could not verify your Google account. Please try signing in again.'
    );
  }

  let payload: GoogleUserInfoResponse;
  try {
    payload = (await response.json()) as GoogleUserInfoResponse;
  } catch {
    throw new RequestFailure(
      'parse',
      'Received an unexpected response from Google.'
    );
  }

  if (!payload.sub) {
    throw new RequestFailure(
      'unknown',
      'Your Google account ID could not be read. Please try again.'
    );
  }

  return {
    socialId: payload.sub,
    googleName: payload.name?.trim() ?? '',
    googleMail: payload.email?.trim() ?? '',
    googlePhoto: payload.picture?.trim() ?? '',
  };
}
